import styled from "styled-components";
import { useEffect, useState } from "react";
import { Radio } from "antd";
import { Form, Button, Input, Row, Col, Select, Checkbox } from "library";
import { Body4 } from "components/Typography";
import { INSTITUCIONES_EIDU, PAISES_EIDU, filtrarPaisOpcion } from "utilites/eiduForm";

const TiposDiscapacidad = [
  { label: "Visual", value: "Visual" },
  { label: "Auditiva", value: "Auditiva" },
  { label: "Motora", value: "Motora" },
  { label: "Psicosocial", value: "Psicosocial" },
  { label: "Sistémica", value: "Sistémica" },
  { label: "Cognitiva-Intelectual", value: "Cognitiva-Intelectual" },
];

const formItemLayout = {
  labelCol: { span: 24 },
  wrapperCol: { span: 24 },
};

interface InscripcionEiduFormProps {
  onFinish: (values: any) => void;
  talleres?: { label: string; value: number; disabled?: boolean }[];
  isSubmitting?: boolean;
  initialValues?: Record<string, unknown>;
  onCancel?: () => void;
  onRefreshTalleres?: () => void;
  submitButtonText?: string;
}

const defaultInitialValues = {
  tieneDiscapacidad: "no",
  requiereApoyo: "no",
};

export function InscripcionEiduForm({
  onFinish,
  talleres = [],
  isSubmitting = false,
  initialValues,
  onCancel,
  onRefreshTalleres,
  submitButtonText = "Completar inscripción",
}: InscripcionEiduFormProps) {
  const mergedInitialValues: Record<string, unknown> = {
    ...defaultInitialValues,
    ...initialValues,
  };
  const [pertenecienteA, setPertenecienteA] = useState<string | undefined>(
    mergedInitialValues.pertenecienteA as string | undefined
  );
  const [tieneDiscapacidad, setTieneDiscapacidad] = useState(
    (mergedInitialValues.tieneDiscapacidad as string) ?? "no"
  );
  const [requiereApoyo, setRequiereApoyo] = useState(
    (mergedInitialValues.requiereApoyo as string) ?? "no"
  );

  useEffect(() => {
    if (!initialValues) return;
    if (initialValues.pertenecienteA != null) {
      setPertenecienteA(initialValues.pertenecienteA as string);
    }
    if (initialValues.tieneDiscapacidad != null) {
      setTieneDiscapacidad(initialValues.tieneDiscapacidad as string);
    }
    if (initialValues.requiereApoyo != null) {
      setRequiereApoyo(initialValues.requiereApoyo as string);
    }
  }, [initialValues]);

  return (
    <FormContainer>
      <Form
        {...formItemLayout}
        onFinish={onFinish}
        name="inscripcion-eidu"
        initialValues={mergedInitialValues}
        onValuesChange={(changed) => {
          if ("pertenecienteA" in changed) {
            setPertenecienteA(changed.pertenecienteA);
          }
          if ("tieneDiscapacidad" in changed) {
            setTieneDiscapacidad(changed.tieneDiscapacidad);
          }
          if ("requiereApoyo" in changed) {
            setRequiereApoyo(changed.requiereApoyo);
          }
        }}
      >
        <Row gutter={32}>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Correo de contacto"
              name="correoContacto"
              rules={[
                { required: true, message: "Campo requerido" },
                { type: "email", message: "Correo inválido" },
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Confirmación de correo de contacto"
              name="confirmacionCorreo"
              dependencies={["correoContacto"]}
              rules={[
                { required: true, message: "Campo requerido" },
                ({ getFieldValue }) => ({
                  validator(_, value) {
                    if (!value || getFieldValue("correoContacto") === value) {
                      return Promise.resolve();
                    }
                    return Promise.reject(
                      new Error("Los correos no coinciden")
                    );
                  },
                }),
              ]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Nombre completo"
              name="nombre"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Identificación personal (Número de cédula)"
              name="identificacion"
              normalize={(value) =>
                value == null ? value : String(value).replace(/\D/g, "")
              }
              rules={[
                { required: true, message: "Campo requerido" },
                {
                  pattern: /^\d+$/,
                  message: "Solo se permiten números",
                },
              ]}
            >
              <Input inputMode="numeric" placeholder="Digite únicamente números" />
            </Form.Item>
            <Form.Item
              label="País"
              name="pais"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Select
                options={PAISES_EIDU}
                placeholder="Seleccionar o buscar país"
                showSearch
                optionFilterProp="label"
                filterOption={filtrarPaisOpcion}
              />
            </Form.Item>
            <Form.Item
              label="Teléfono de contacto (incluir código de país)"
              name="telefono"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Input placeholder="Ej: +506 8888-8888" />
            </Form.Item>
            <Form.Item
              label="Perteneciente a"
              name="pertenecienteA"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Select
                options={INSTITUCIONES_EIDU}
                placeholder="Seleccionar institución"
              />
            </Form.Item>
            {pertenecienteA === "Otro" && (
              <Form.Item
                label="Indique la institución"
                name="pertenecienteAOtro"
                rules={[{ required: true, message: "Campo requerido" }]}
              >
                <Input />
              </Form.Item>
            )}
          </Col>
          <Col xs={24} lg={12}>
            <Form.Item
              label="Unidad Académica (Facultad o Escuela)"
              name="unidad"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Input />
            </Form.Item>
            <Form.Item
              label="Programa Académico o Carrera"
              name="carrera"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Input />
            </Form.Item>

            <Form.Item label="Talleres de formación" name="taller">
              <Select
                allowClear
                options={talleres}
                placeholder="Sin taller / seleccionar uno"
                onDropdownVisibleChange={(open: boolean) => {
                  if (open) onRefreshTalleres?.();
                }}
              />
            </Form.Item>
            <Body4 style={{ color: "#000000", marginBottom: 24 }}>
              Si desea participar en alguno de los talleres o webinars, puede
              solicitar un cupo en esta sección. Todos los talleres y webinars
              serán a las 8:30 a.m. Costa Rica | 9:30 a.m. Colombia. Los
              talleres tienen un cupo limitado y se asignan por orden de
              solicitud.
            </Body4>

            <Form.Item
              label="¿Tiene alguna discapacidad?"
              name="tieneDiscapacidad"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Radio.Group>
                <Radio value="si">Sí</Radio>
                <Radio value="no">No</Radio>
              </Radio.Group>
            </Form.Item>
            {tieneDiscapacidad === "si" && (
              <Form.Item
                label='De haber respondido "Sí", indique el tipo'
                name="tipoDiscapacidad"
                rules={[
                  {
                    required: true,
                    message: "Seleccione al menos un tipo",
                  },
                ]}
              >
                <Checkbox.Group options={TiposDiscapacidad} />
              </Form.Item>
            )}

            <Form.Item
              label="¿Requiere algún tipo de apoyo para su participación?"
              name="requiereApoyo"
              rules={[{ required: true, message: "Campo requerido" }]}
            >
              <Radio.Group>
                <Radio value="si">Sí</Radio>
                <Radio value="no">No</Radio>
              </Radio.Group>
            </Form.Item>
            {requiereApoyo === "si" && (
              <Form.Item
                label="Indique cuál"
                name="detalleApoyo"
                rules={[{ required: true, message: "Campo requerido" }]}
              >
                <Input />
              </Form.Item>
            )}

            <ButtonContainer>
              {onCancel && (
                <Button onClick={onCancel} variant="secondary">
                  Cancelar
                </Button>
              )}
              <Button htmlType="submit" disabled={isSubmitting} block={!onCancel}>
                {isSubmitting ? "Enviando..." : submitButtonText}
              </Button>
            </ButtonContainer>
          </Col>
        </Row>
      </Form>
    </FormContainer>
  );
}

const FormContainer = styled.div`
  .ant-form-item {
    margin-bottom: 24px;
    position: relative;
  }
  .ant-form-item-label {
    padding-bottom: 0;
  }
  .ant-checkbox-group {
    display: flex;
    flex-direction: column;
    gap: 8px;
  }
  .ant-checkbox-inner {
    border-color: #000000 !important;
  }
  .ant-checkbox:hover .ant-checkbox-inner,
  .ant-checkbox-wrapper:hover .ant-checkbox-inner,
  .ant-checkbox-input:focus + .ant-checkbox-inner {
    border-color: #000000 !important;
  }
  .ant-checkbox-checked .ant-checkbox-inner {
    background-color: #000000 !important;
    border-color: #000000 !important;
  }
  .ant-checkbox-checked::after {
    border-color: #000000 !important;
  }
  .ant-checkbox-wrapper,
  .ant-radio-wrapper {
    font-weight: normal;
    color: #000000;
  }
  label {
    font-size: 12px;
    line-height: 19px;
    font-weight: normal;
    @media (min-width: 768px) {
      font-size: 16px;
      line-height: 22px;
    }
  }
`;

const ButtonContainer = styled.div`
  display: flex;
  gap: 16px;
  button {
    flex: 1;
  }
`;
